import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from '@clofo/i18n'
import { clofoApi, ClofoApiError } from '@clofo/data/api'
import { ls } from '@clofo/data/local-storage'
import { FormInput, FormCheckbox } from '@clofo/ui/controlled-field'
import { SubmitButton } from '@clofo/ui/submit-button'
import { Button } from '@clofo/ui/button'

import { getRedirectTo } from '../lib/redirect'

type RequestPasswordFormProps = {
  email: string
  /** Return to the email step (the "change" affordance). */
  onChangeEmail: () => void
}

/**
 * Second step of the adaptive login flow, shown when the email's domain is not SSO-enabled. The
 * user confirms their password; on success the obtain endpoint sets the refresh cookie and we
 * redirect to the target app. Mirrors the legacy login app's `request-password` state.
 */
export const RequestPasswordForm = ({ email, onChangeEmail }: RequestPasswordFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const schema = z.object({
    password: z.string().min(1, { error: t('errors.present') }),
    remember: z.boolean(),
  })
  type Values = z.infer<typeof schema>

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', remember: false },
  })

  const onSubmit = async ({ password, remember }: Values) => {
    setIsSubmitting(true)
    try {
      const token = await clofoApi.auth.loginWithPassword({ email, password, remember })
      // A token that still requires a second factor is kept for the TFA pages, which read the
      // status (and, for first-time setup, the tfa_secret_uri) from it. Legacy naming is
      // counter-intuitive: 'enforced' = TFA already configured, 'enabled' = still needs setup.
      if (token.tfa_status === 'enforced' || token.tfa_status === 'enabled') {
        ls.setItem('clofoToken', token)
        navigate(token.tfa_status === 'enforced' ? '/tfa/verify' : '/tfa/confirm')
        return
      }
      window.location.href = getRedirectTo()
    } catch (error) {
      setIsSubmitting(false)
      const errorCode = error instanceof ClofoApiError ? error.errorCode : null
      form.setError('password', {
        message:
          errorCode === 'locked'
            ? t('login.errors.locked')
            : errorCode === 'bad_credentials'
              ? t('login.errors.invalidCredentials')
              : t('login.errors.generic'),
      })
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col max-w-sm w-full'>
      <p className='text-secondary-foregroundss text-xs mb-1'>{t('login.siginAs')}</p>
      <p className='mb-3 flex items-baseline gap-2'>
        <span className='font-semibold text-lg'>{email}</span>
        <Button
          type='button'
          size='sm'
          variant='link'
          onClick={onChangeEmail}
          className='h-auto px-1 text-xs font-normal'
        >
          {t('login.change')}
        </Button>
      </p>

      <FormInput
        control={form.control}
        name='password'
        type='password'
        label={t('login.passwordPlaceholder')}
        hideLabel
        placeholder={t('login.passwordPlaceholder')}
        autoComplete='current-password'
        autoFocus
        className={{ input: 'h-15' }}
      />

      <FormCheckbox
        control={form.control}
        name='remember'
        label={t('login.rememberMe')}
        className={{ field: 'mt-4' }}
      />

      <SubmitButton type='submit' size='lg' className='mt-6' isSubmitting={isSubmitting}>
        {t('login.buttonLogin')}
      </SubmitButton>
      <Button asChild variant='link' className='mt-8 font-normal w-fit mx-auto'>
        <Link to='/passwords/forgot' state={{ email }}>
          {t('login.forgotPassword')}
        </Link>
      </Button>
    </form>
  )
}
